import React, { useState } from 'react';
import { Users, Target, ChevronDown, ChevronUp, Building2, User, Pencil, X, Check, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export default function PersonasDisplay({ personas, onUpdate, saving }) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [editingPersona, setEditingPersona] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(false);
  
  // Handle case where personas is null/undefined
  const businessModel = personas?.business_model_classification || {
    primary_model: '',
    sales_motion: '',
    buyer_vs_user: '',
    price_sensitivity: ''
  };
  const primaryPersonas = personas?.primary_personas || [];
  
  const handleUpdateBusinessModel = (field, value) => {
    const updated = {
      ...personas,
      business_model_classification: {
        ...businessModel,
        [field]: value
      }
    };
    onUpdate?.(updated);
  };
  
  const handleUpdatePersona = (index, updatedPersona) => {
    const newPersonas = [...primaryPersonas];
    newPersonas[index] = updatedPersona;
    const updated = { ...personas, primary_personas: newPersonas };
    onUpdate?.(updated);
  };
  
  const handleAddPersona = () => {
    const newPersona = {
      persona_name: 'New Persona',
      persona_type: 'primary',
      confidence_level: 'medium',
      core_intent: { primary_job_to_be_done: '' },
      key_pain_points: [],
      primary_triggers: [],
      messaging_that_resonates: [],
      ctas_most_likely_to_convert: []
    };
    const newPersonas = [...primaryPersonas, newPersona];
    const updated = { ...personas, primary_personas: newPersonas };
    onUpdate?.(updated);
    setExpandedIndex(newPersonas.length - 1);
    setEditingPersona(newPersonas.length - 1);
  };
  
  const handleDeletePersona = (index) => {
    if (!window.confirm('Delete this persona?')) return;
    const newPersonas = primaryPersonas.filter((_, i) => i !== index);
    const updated = { ...personas, primary_personas: newPersonas };
    onUpdate?.(updated);
    setExpandedIndex(null);
    setEditingPersona(null);
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 mb-6" data-testid="personas-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Customer Personas</h2>
            <p className="text-sm text-muted-foreground">
              {primaryPersonas.length > 0 ? `${primaryPersonas.length} persona${primaryPersonas.length !== 1 ? 's' : ''} identified` : 'No personas yet'}
            </p>
          </div>
        </div>
        {onUpdate && (
          <Button onClick={handleAddPersona} variant="outline" size="sm" className="rounded-full" data-testid="add-persona-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Persona
          </Button>
        )}
      </div>

      <BusinessModelCard 
        model={businessModel} 
        editing={editingBusiness}
        onEdit={() => setEditingBusiness(true)}
        onSave={() => setEditingBusiness(false)}
        onUpdate={handleUpdateBusinessModel}
        canEdit={!!onUpdate}
      />

      {primaryPersonas.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Target className="w-4 h-4" />
            Primary Personas ({primaryPersonas.length})
          </h3>
          <PersonasList 
            personas={primaryPersonas} 
            expandedIndex={expandedIndex}
            setExpandedIndex={setExpandedIndex}
            editingPersona={editingPersona}
            setEditingPersona={setEditingPersona}
            onUpdate={onUpdate ? handleUpdatePersona : null}
            onDelete={onUpdate ? handleDeletePersona : null}
          />
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No personas identified yet.</p>
          <p className="text-xs mt-1">Click "Add Persona" to create one manually, or re-scan the website.</p>
        </div>
      )}
    </div>
  );
}

function BusinessModelCard({ model, editing, onEdit, onSave, onUpdate, canEdit }) {
  return (
    <div className="mb-6 p-4 bg-muted/30 rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Business Model</span>
        </div>
        {canEdit && !editing && (
          <Button onClick={onEdit} variant="ghost" size="sm">
            <Pencil className="w-3 h-3" />
          </Button>
        )}
        {canEdit && editing && (
          <Button onClick={onSave} variant="ghost" size="sm">
            <Check className="w-3 h-3" />
          </Button>
        )}
      </div>
      
      {editing ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <label className="text-xs text-muted-foreground">Primary Model</label>
            <Input 
              value={model.primary_model || ''} 
              onChange={(e) => onUpdate('primary_model', e.target.value)}
              className="h-8 text-sm mt-1"
              placeholder="e.g., B2C, B2B"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sales Motion</label>
            <Input 
              value={model.sales_motion || ''} 
              onChange={(e) => onUpdate('sales_motion', e.target.value)}
              className="h-8 text-sm mt-1"
              placeholder="e.g., self-serve"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Buyer/User</label>
            <Input 
              value={model.buyer_vs_user || ''} 
              onChange={(e) => onUpdate('buyer_vs_user', e.target.value)}
              className="h-8 text-sm mt-1"
              placeholder="e.g., same, different"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Price Sensitivity</label>
            <Input 
              value={model.price_sensitivity || ''} 
              onChange={(e) => onUpdate('price_sensitivity', e.target.value)}
              className="h-8 text-sm mt-1"
              placeholder="e.g., high, medium, low"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Primary:</span>
            <span className="ml-2 font-medium text-foreground">{model.primary_model || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Sales:</span>
            <span className="ml-2 font-medium text-foreground capitalize">{model.sales_motion || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Buyer/User:</span>
            <span className="ml-2 font-medium text-foreground capitalize">{model.buyer_vs_user || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Price Sensitivity:</span>
            <span className="ml-2 font-medium text-foreground capitalize">{model.price_sensitivity || '-'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonasList({ personas, expandedIndex, setExpandedIndex, editingPersona, setEditingPersona, onUpdate, onDelete }) {
  return (
    <div className="space-y-3">
      {personas.map((persona, index) => (
        <PersonaCard 
          key={index}
          persona={persona}
          index={index}
          isExpanded={expandedIndex === index}
          isEditing={editingPersona === index}
          onToggle={() => setExpandedIndex(expandedIndex === index ? null : index)}
          onEdit={() => setEditingPersona(index)}
          onSaveEdit={() => setEditingPersona(null)}
          onUpdate={onUpdate ? (updated) => onUpdate(index, updated) : null}
          onDelete={onDelete ? () => onDelete(index) : null}
        />
      ))}
    </div>
  );
}

function PersonaCard({ persona, index, isExpanded, isEditing, onToggle, onEdit, onSaveEdit, onUpdate, onDelete }) {
  const getConfidenceColor = (level) => {
    if (level === 'high') return 'bg-green-100 text-green-700';
    if (level === 'medium') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`persona-card-${index}`}>
      <div
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground">{persona.persona_name}</h4>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2 py-0.5 rounded-full ${getConfidenceColor(persona.confidence_level)}`}>
                {persona.confidence_level} confidence
              </span>
              <span className="text-xs text-muted-foreground capitalize">{persona.persona_type}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onDelete && (
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded-lg hover:bg-red-100 text-muted-foreground hover:text-red-600 transition-colors"
              data-testid={`delete-persona-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </div>

      {isExpanded && (
        <PersonaDetails 
          persona={persona} 
          isEditing={isEditing}
          onEdit={onEdit}
          onSaveEdit={onSaveEdit}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

function PersonaDetails({ persona, isEditing, onEdit, onSaveEdit, onUpdate }) {
  const updateField = (field, value) => {
    onUpdate?.({ ...persona, [field]: value });
  };
  
  const updateNestedField = (parent, field, value) => {
    onUpdate?.({ 
      ...persona, 
      [parent]: { ...persona[parent], [field]: value } 
    });
  };

  return (
    <div className="px-4 pb-4 space-y-4 border-t border-border bg-muted/10">
      {/* Edit toggle */}
      {onUpdate && (
        <div className="pt-4 flex justify-end">
          {!isEditing ? (
            <Button onClick={onEdit} variant="outline" size="sm">
              <Pencil className="w-3 h-3 mr-2" /> Edit Persona
            </Button>
          ) : (
            <Button onClick={onSaveEdit} size="sm" className="bg-primary text-white">
              <Check className="w-3 h-3 mr-2" /> Done Editing
            </Button>
          )}
        </div>
      )}
      
      {/* Persona Name */}
      {isEditing && (
        <div className="pt-2">
          <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Persona Name</h5>
          <Input
            value={persona.persona_name || ''}
            onChange={(e) => updateField('persona_name', e.target.value)}
            className="h-9"
          />
        </div>
      )}
      
      {/* Confidence & Type */}
      {isEditing && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Confidence Level</h5>
            <select
              value={persona.confidence_level || 'medium'}
              onChange={(e) => updateField('confidence_level', e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Type</h5>
            <select
              value={persona.persona_type || 'primary'}
              onChange={(e) => updateField('persona_type', e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="aspirational">Aspirational</option>
            </select>
          </div>
        </div>
      )}

      {/* Core Intent */}
      <div className={!isEditing ? "pt-4" : ""}>
        <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Core Intent</h5>
        {isEditing ? (
          <Textarea
            value={persona.core_intent?.primary_job_to_be_done || ''}
            onChange={(e) => updateNestedField('core_intent', 'primary_job_to_be_done', e.target.value)}
            className="min-h-[60px] text-sm"
            placeholder="What is the primary job to be done?"
          />
        ) : (
          <p className="text-sm text-foreground">{persona.core_intent?.primary_job_to_be_done || 'Not specified'}</p>
        )}
      </div>

      {/* Pain Points */}
      <div>
        <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Pain Points</h5>
        {isEditing ? (
          <EditableList 
            items={persona.key_pain_points || []} 
            onChange={(items) => updateField('key_pain_points', items)}
            placeholder="Add a pain point"
          />
        ) : (
          <PainPointsList items={persona.key_pain_points || []} />
        )}
      </div>

      {/* Triggers */}
      <div>
        <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Triggers</h5>
        {isEditing ? (
          <EditableList 
            items={persona.primary_triggers || []} 
            onChange={(items) => updateField('primary_triggers', items)}
            placeholder="Add a trigger"
          />
        ) : (
          <TagsList items={persona.primary_triggers || []} variant="green" />
        )}
      </div>

      {/* Messaging */}
      <div>
        <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Messaging That Resonates</h5>
        {isEditing ? (
          <EditableList 
            items={persona.messaging_that_resonates || []} 
            onChange={(items) => updateField('messaging_that_resonates', items)}
            placeholder="Add a message"
          />
        ) : (
          <MessagesList items={persona.messaging_that_resonates || []} />
        )}
      </div>

      {/* CTAs */}
      <div>
        <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Best CTAs</h5>
        {isEditing ? (
          <EditableList 
            items={persona.ctas_most_likely_to_convert || []} 
            onChange={(items) => updateField('ctas_most_likely_to_convert', items)}
            placeholder="Add a CTA"
          />
        ) : (
          <TagsList items={persona.ctas_most_likely_to_convert || []} variant="primary" />
        )}
      </div>
    </div>
  );
}

function EditableList({ items, onChange, placeholder }) {
  const addItem = () => onChange([...items, '']);
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(newItems);
  };
  const removeItem = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={item}
            onChange={(e) => updateItem(i, e.target.value)}
            className="flex-1 h-8 text-sm"
            placeholder={placeholder}
          />
          <button onClick={() => removeItem(i)} className="p-1 text-muted-foreground hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button onClick={addItem} variant="ghost" size="sm" className="text-xs">
        <Plus className="w-3 h-3 mr-1" /> Add
      </Button>
    </div>
  );
}

function PainPointsList({ items }) {
  if (!items || items.length === 0) return <p className="text-xs text-muted-foreground italic">None specified</p>;
  return (
    <ul className="text-xs text-foreground space-y-1">
      {items.map((item, i) => <li key={i}>• {item}</li>)}
    </ul>
  );
}

function MessagesList({ items }) {
  if (!items || items.length === 0) return <p className="text-xs text-muted-foreground italic">None specified</p>;
  return (
    <ul className="text-xs text-foreground space-y-1">
      {items.map((item, i) => <li key={i}>✓ {item}</li>)}
    </ul>
  );
}

function TagsList({ items, variant }) {
  if (!items || items.length === 0) return <p className="text-xs text-muted-foreground italic">None specified</p>;
  const colorClass = variant === 'green' 
    ? 'bg-green-100 text-green-700' 
    : 'bg-primary/10 text-primary';
    
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, i) => (
        <span key={i} className={`text-xs px-2 py-1 rounded ${colorClass}`}>{item}</span>
      ))}
    </div>
  );
}
